import { metaData } from '@cornerstonejs/core';
import { classes } from '@ohif/core';

/**
 * Above OHIF's own provider, which `extensions/cornerstone/src/init.tsx` registers at
 * 9999. Cornerstone queries providers highest-priority-first and returns the first
 * non-undefined answer, so this one gets to intervene before OHIF's is consulted.
 */
export const GLEAMER_METADATA_PRIORITY = 10000;

/**
 * Array-wraps a single-valued `voiLUTFunction`.
 *
 * This lived as a patch inside `platform/core/src/classes/MetadataProvider.ts`. It is
 * a provider here instead, because Cornerstone already exposes exactly the seam it
 * needs and a fork patch in core has to be re-applied — and re-verified — at every
 * upstream merge.
 *
 * Why it exists (DATAT-482, production-affecting): dcmjs 0.52 naturalizes a
 * single-valued VOILUTFunction (0028,1056) to a bare string such as "SIGMOID". CS3D 5's
 * dicom-image-loader reads `voiLutModule.voiLUTFunction[0]`, so a bare string yields
 * "S" — an invalid function. The normal display path normalises that
 * (`_getValidVOILUTFunction`), but the VOI-cache-restore path taken on a cross-series
 * jump (`_getVOIRangeForCurrentImage`) does not, and threw "Invalid VOI LUT function".
 *
 * Returns `undefined` in every case that needs no correction, so the query falls
 * through to OHIF's provider and this stays a narrow interception rather than a second
 * implementation of `voiLutModule` that could drift from upstream's.
 */
export function voiLutFunctionProvider(query: string, imageId: string) {
  if (query !== 'voiLutModule' || !imageId || Array.isArray(imageId)) {
    return undefined;
  }

  const base = classes.MetadataProvider.get(query, imageId);
  const voiLUTFunction = base?.voiLUTFunction;

  // Absent stays absent. Wrapping `undefined` into `[undefined]` would turn a
  // cleanly-missing function into a present-but-invalid one downstream.
  if (voiLUTFunction == null || Array.isArray(voiLUTFunction)) {
    return undefined;
  }

  return { ...base, voiLUTFunction: [voiLUTFunction] };
}

/**
 * Registered from `gdata-extension`'s `preRegistration`. `addProvider` de-duplicates by
 * function identity, so calling this more than once is harmless.
 */
export function registerGleamerMetadataProviders() {
  metaData.addProvider(voiLutFunctionProvider, GLEAMER_METADATA_PRIORITY);
}
